import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IProviderProps } from "../interfaces/provider";
import { apiContext } from "./api";
import { IAvantCareEnvironmentInfo } from "../interfaces/resource";
import {
    cloneRsoConstraintLevels,
    createDefaultRsoConstraintLevels,
    IRsoConfigurationPreset,
    IRsoConstraintLevels,
    normalizeRsoConstraintLevels,
    RsoConfigurationSource
} from "../interfaces/rsoConfiguration";
import { toast } from "../helpers/notifications";
import { rsoConfigurationContext } from "./rsoConfigurationContext";
import { RsoConfigurationModal } from "../components/rsoConfiguration/RsoConfigurationModal";

const extractPresetArray = (response: any): any[] => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.value)) return response.value;
    if (Array.isArray(response?.results)) return response.results;
    if (Array.isArray(response?.items)) return response.items;
    return [];
};

const normalizePresetRecord = (raw: any): IRsoConfigurationPreset | null => {
    if (!raw) return null;
    const id = String(raw?.id ?? raw?.illumina_rsoconfigurationpresetid ?? raw?.rsoConfigurationPresetId ?? '').trim();
    const name = String(raw?.name ?? raw?.illumina_name ?? '').trim();
    if (!id || !name) return null;

    let levelsSource: any = raw?.levels ?? raw?.rsoConstraintLevels ?? raw?.illumina_value;
    if (typeof levelsSource === 'string') {
        try {
            levelsSource = JSON.parse(levelsSource);
        } catch {
            levelsSource = null;
        }
    }
    if (levelsSource && typeof levelsSource === 'object' && 'rsoConstraintLevels' in levelsSource) {
        levelsSource = levelsSource.rsoConstraintLevels;
    }

    return {
        id,
        name,
        description: String(raw?.description ?? raw?.illumina_description ?? '').trim() || null,
        levels: normalizeRsoConstraintLevels(levelsSource),
        createdOn: raw?.createdOn ?? raw?.createdon ?? null,
        modifiedOn: raw?.modifiedOn ?? raw?.modifiedon ?? null
    };
};

const normalizeEnvironmentLevels = (response: any): IRsoConstraintLevels => {
    return normalizeRsoConstraintLevels(response?.configuration?.rosterBoardModule?.rsoConstraintLevels);
};

const RsoConfigurationProvider = (props: IProviderProps) => {
    const { getOrThrow, postOrThrow, putOrThrow, removeOrThrow } = useContext(apiContext);
    const [defaultLevels, setDefaultLevels] = useState<IRsoConstraintLevels>(createDefaultRsoConstraintLevels());
    const [savedPresets, setSavedPresets] = useState<IRsoConfigurationPreset[]>([]);
    const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
    const [manualOverrideLevels, setManualOverrideLevelsState] = useState<IRsoConstraintLevels | null>(null);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const setManualOverrideLevels = useCallback((levels: IRsoConstraintLevels | null) => {
        setManualOverrideLevelsState(levels ? normalizeRsoConstraintLevels(levels) : null);
    }, []);

    const reloadRsoDefaults = useCallback(async () => {
        try {
            const response = await getOrThrow('/AvantCare/environment');
            setDefaultLevels(normalizeEnvironmentLevels(response));
            setLoadError(null);
        } catch (error: any) {
            const message = String(error?.message ?? error ?? 'Unable to load RSO defaults.');
            setLoadError(message);
            toast('error', 'Unable to load RSO defaults.');
            setDefaultLevels(createDefaultRsoConstraintLevels());
        }
    }, [getOrThrow]);

    const reloadPresets = useCallback(async (): Promise<IRsoConfigurationPreset[]> => {
        setIsBusy(true);
        try {
            const response = await getOrThrow('/RsoConfiguration/presets');
            const normalized = extractPresetArray(response)
                .map(normalizePresetRecord)
                .filter((preset): preset is IRsoConfigurationPreset => !!preset);
            setSavedPresets(normalized);
            setLoadError(null);
            return normalized;
        } catch (error: any) {
            const message = String(error?.message ?? error ?? 'Unable to load saved RSO presets.');
            setLoadError(message);
            toast('error', 'Unable to load saved RSO presets.');
            setSavedPresets([]);
            return [];
        } finally {
            setIsBusy(false);
        }
    }, [getOrThrow]);

    useEffect(() => {
        void reloadRsoDefaults();
        void reloadPresets();
    }, [reloadPresets, reloadRsoDefaults]);

    useEffect(() => {
        if (!selectedPresetId) return;
        if (savedPresets.some((preset) => preset.id === selectedPresetId)) return;
        setSelectedPresetId(null);
    }, [savedPresets, selectedPresetId]);

    const selectedPreset = useMemo(
        () => savedPresets.find((preset) => preset.id === selectedPresetId) ?? null,
        [savedPresets, selectedPresetId]
    );

    const activeLevels = useMemo(() => {
        if (manualOverrideLevels) return cloneRsoConstraintLevels(manualOverrideLevels);
        if (selectedPreset) return cloneRsoConstraintLevels(selectedPreset.levels);
        return cloneRsoConstraintLevels(defaultLevels);
    }, [defaultLevels, manualOverrideLevels, selectedPreset]);

    const activeSource: RsoConfigurationSource = manualOverrideLevels
        ? 'manual'
        : selectedPreset
            ? 'saved'
            : 'default';

    const activePresetLabel = useMemo(() => {
        if (activeSource === 'manual') return 'Manual for This Run';
        if (selectedPreset?.name) return selectedPreset.name;
        return 'Default';
    }, [activeSource, selectedPreset]);

    const openRsoConfiguration = useCallback(() => {
        setIsEditorOpen(true);
    }, []);

    const closeRsoConfiguration = useCallback(() => {
        setIsEditorOpen(false);
    }, []);

    const clearManualOverrideLevels = useCallback(() => {
        setManualOverrideLevelsState(null);
    }, []);

    const savePreset = useCallback(async (args: {
        id?: string;
        name: string;
        description?: string | null;
        levels: IRsoConstraintLevels;
    }): Promise<IRsoConfigurationPreset | null> => {
        const payload = {
            name: String(args.name ?? '').trim(),
            description: String(args.description ?? '').trim() || null,
            levels: normalizeRsoConstraintLevels(args.levels)
        };

        if (!payload.name) {
            toast('error', 'Preset name is required.');
            return null;
        }

        setIsBusy(true);
        try {
            const response = args.id
                ? await putOrThrow(`/RsoConfiguration/presets/${encodeURIComponent(args.id)}`, payload)
                : await postOrThrow('/RsoConfiguration/presets', payload);

            const refreshedPresets = await reloadPresets();
            const savedPreset = normalizePresetRecord(response)
                ?? (args.id ? refreshedPresets.find((preset) => preset.id === args.id) ?? null : null)
                ?? refreshedPresets.find((preset) => preset.name === payload.name && JSON.stringify(preset.levels) === JSON.stringify(payload.levels)) ?? null;
            if (!savedPreset) {
                toast('error', 'Preset was not saved.');
                return null;
            }
            if (savedPreset?.id) {
                setSelectedPresetId(savedPreset.id);
            } else if (args.id) {
                setSelectedPresetId(args.id);
            }
            setManualOverrideLevelsState(null);
            setIsEditorOpen(false);
            return savedPreset;
        } catch (error: any) {
            const message = String(error?.message ?? error ?? 'Failed to save preset.');
            toast('error', message);
            return null;
        } finally {
            setIsBusy(false);
        }
    }, [postOrThrow, putOrThrow, reloadPresets]);

    const deletePreset = useCallback(async (presetId: string) => {
        const resolvedId = String(presetId ?? '').trim();
        if (!resolvedId) return;
        setIsBusy(true);
        try {
            await removeOrThrow(`/RsoConfiguration/presets/${encodeURIComponent(resolvedId)}`);
            const refreshedPresets = await reloadPresets();
            if (refreshedPresets.some((preset) => preset.id === resolvedId)) {
                toast('error', 'Preset was not deleted.');
                return;
            }
            if (selectedPresetId === resolvedId) {
                setSelectedPresetId(null);
            }
            setManualOverrideLevelsState(null);
            setIsEditorOpen(false);
        } catch (error: any) {
            const message = String(error?.message ?? error ?? 'Failed to delete preset.');
            toast('error', message);
        } finally {
            setIsBusy(false);
        }
    }, [reloadPresets, removeOrThrow, selectedPresetId]);

    const value = useMemo(() => ({
        defaultLevels,
        savedPresets,
        selectedPresetId,
        manualOverrideLevels,
        activeLevels,
        activeSource,
        activePresetLabel,
        isEditorOpen,
        isBusy,
        loadError,
        openRsoConfiguration,
        closeRsoConfiguration,
        setSelectedPresetId,
        setManualOverrideLevels,
        clearManualOverrideLevels,
        savePreset,
        deletePreset,
        reloadPresets
    }), [
        activeLevels,
        activePresetLabel,
        activeSource,
        clearManualOverrideLevels,
        deletePreset,
        defaultLevels,
        isBusy,
        isEditorOpen,
        loadError,
        manualOverrideLevels,
        openRsoConfiguration,
        reloadPresets,
        savePreset,
        savedPresets,
        setManualOverrideLevels,
        selectedPresetId
    ]);

    return (
        <rsoConfigurationContext.Provider value={value}>
            <RsoConfigurationModal />
            {props.children}
        </rsoConfigurationContext.Provider>
    );
};

export { RsoConfigurationProvider };
